import EventEmitter from 'eventemitter3';
import { Data } from '../models/data';
import { Order } from '../models/order';
import { User } from '../models/user';
import {
    IUser, IUserCampaignCodesData, IUserCard, IUserFavoriteItem, IUserLoyaltyReward,
    IUserOffers, IUserRewardOffersDTO, ISingleUseCouponWalletEntry
} from '../models/user.interface';
import { 
    IaOLOUser, IaOLOUserFavoriteStore, IaOLOUserLoyaltyReward, IaOLOUserCouponAvailability,
    IaOLOUserCoupon, IaOLOUserNextOrderCredit 
} from '../models/user-olo.interface';
import { IDataReward } from "../models/data.interface";
import { ILoyaltyProvider } from './loyalty-provider/loyalty-provider.interface';
import { GoogleSignInService } from './google-signin-service';

import { IMyHistory } from '../components/pages/Rewards/rewards.interface';
import {
    ISignIn, IChangePassword, IFetchFavoriteStoresResponse, IFetchOrderDetails, IGetProfileResponse,
    IGetPhoneCodeResponse, IForgotPassword, ISignInPhone,
    ISignInResponse,
    MfaMessage,
    IResetPassword,
    IResetPasswordResponse,
    IUnsubscribeDto,
    IAccountMfaStart
} from './profile-service.interface';
import { IProfile, IProfileAddress, IProfileAllergy, IProfileDietary } from './profile.interfaces';
import { Util } from '../utils/util';
import { CouponIdList, HttpStatusCode, LoyaltyProvider, Pages } from '../types/enums';
import { ITempAddress } from '../interfaces/temp.interface';
import { InOrderAddress } from '../online-ordering/interfaces/online-ordering.interface';
import { EventTypes } from '../core/event-bus';
import { LoaderSpinner } from '../components/shared/LoaderSpinner/loader-spinner';
import { Context } from '../models/context';
import { HttpService, IHttpRequestOptions } from './http-service';
import { TokenService } from './token-service';
import { IService } from './service.interface';
import { IMfaPrompt, MfaPromptPurpose, MfaVerifyOutcome } from '../components/modals/MfaVerify/mfa-verify.interface';

/**
 * A service class that provides methods to manage user profile related functionality.
 * @class
 */
export class ProfileService implements IService {
    private readonly _http: HttpService;
    private readonly _eventBus: EventEmitter;
    private _data: Data;
    private _context: Context;
    private _user: User;
    private _order: Order;
    private _apiProfile: string;
    private _apiLoyalty: string;
    private _publicKey: string;
    private _loyaltyProvider: ILoyaltyProvider;
    private _mfaPrompt: IMfaPrompt | null = null;
    private _accountMfaInFlight = false;

    constructor(http: HttpService, eventBus: EventEmitter, data: Data, context: Context, user: User, order: Order, loyaltyProvider: ILoyaltyProvider, apiProfile: string, apiLoyalty: string, publicKey: string) {
        this._http = http;
        this._eventBus = eventBus;
        this._apiProfile = apiProfile;
        this._apiLoyalty = apiLoyalty;
        this._data = data;
        this._context = context;
        this._user = user;
        this._order = order;
        this._publicKey = publicKey;
        this._loyaltyProvider = loyaltyProvider;
    }

    get user(): User {
        return this._user;
    }

    get loyaltyProvider(): ILoyaltyProvider {
        return this._loyaltyProvider;
    }

    public httpRequestAsync(method: string, url: string, payload: object | null = null, options?: IHttpRequestOptions): Promise<Response> {
        const storeKey = this._data.getProperty("StoreInfo")?.storeKey
            ?? this._data.getBrandProperty("StoreInfo").storeKey;

        const timeOffset = this._data.getProperty("TimeOffset")
            ?? this._data.getBrandProperty("TimeOffset");

        const businessStartMinute = this._data.getProperty("Settings")?.BSM
            ?? this._data.getBrandProperty("Settings").BSM;

        let businessDate = new Date().toISOString();
        try {
            businessDate = Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute).toISOString();
        } catch { }

        return this._http.request(method, url, payload, storeKey, businessDate, options);
    }

    public refreshToken = async (): Promise<void> => {
        await this.httpRequestAsync("GET", `${this._apiProfile}refresh_token`);
    }

    /**
     * For a Google sign-up, pass the ID token: the server re-verifies it and takes the email from
     * the token, so the client can't claim an address Google didn't vouch for.
     */
    public readonly signUp = async (profile: IProfile, googleIdToken?: string | null, googleNonce?: string | null): Promise<boolean> => {
        const cp = this._mapProfile(profile)
        const payload = await Util.encrypt(JSON.stringify({ storeKey: this._data.getBrandProperty("StoreInfo").storeKey, location: "olo", profile: cp, id_token: googleIdToken ?? null, nonce: googleNonce ?? null }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiLoyalty}olo/sign_up`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json?.success ?? false;
    }

    private async _handleSignInResponseAsync(response: ISignInResponse): Promise<boolean> {
        if (response.success) {
            if (response.jwt) {
                TokenService.setJWT(response.jwt);
                if (Util.isAppView() && typeof saveAppUser == "function") 
                    saveAppUser(response.jwt);
            }

            response.refreshToken && TokenService.setRefreshToken(response.refreshToken)

            if (response.user) {
                this._user.user = this._mapUser(response.user);
                this._user.setLoggedIn(true);
                await this.getUserWalletAsync();
                // Not awaited -- Customer.Api must never add latency to sign-in.
                void this.getSingleUseCouponWalletAsync();
            }
        }

        return response.success;
    }

    /** The UI that runs the sign-in MFA prompt; set once the modal exists. */
    public setMfaPrompt(prompt: IMfaPrompt): void {
        this._mfaPrompt = prompt;
    }

    /** Returns the response, not a boolean, so callers can tell a closed MFA prompt from a failed sign-in. */
    public readonly signIn = async (data: ISignIn): Promise<ISignInResponse> => {
        const response = await this._loyaltyProvider.signIn({
            ...data,
            mfaTrustToken: TokenService.getMfaTrustToken(),
            mfaSessionToken: TokenService.getMfaSessionToken()
        });

        const result = response.mfaChallengeId
            ? await this._completeMfaSignInAsync(response.mfaChallengeId)
            : response;

        await this._handleSignInResponseAsync(result);
        return result;
    }

    private async _completeMfaSignInAsync(challengeId: string): Promise<ISignInResponse> {
        if (!this._mfaPrompt)
            return { success: false };

        const storeKey = this._data.getBrandProperty("StoreInfo").storeKey;
        let verified: ISignInResponse = { success: false };

        const completed = await this._mfaPrompt.promptAsync({
            purpose: MfaPromptPurpose.SignIn,
            sendCode: (contact: string) => this._loyaltyProvider.sendMfaCode(challengeId, contact),
            verifyCode: async (code: string, trustBrowser: boolean) => {
                verified = await this._loyaltyProvider.verifyMfaCode({ store_key: storeKey, challenge_id: challengeId, code: code, trust_browser: trustBrowser });
                return ProfileService._toMfaOutcome(verified);
            }
        });

        if (!completed)
            return { success: false, mfaCancelled: true };

        if (verified.mfaSessionToken)
            TokenService.setMfaSessionToken(verified.mfaSessionToken);
        if (verified.mfaTrustToken)
            TokenService.setMfaTrustToken(verified.mfaTrustToken);

        return verified;
    }

    private static _toMfaOutcome(response: ISignInResponse): MfaVerifyOutcome {
        if (response.success)
            return MfaVerifyOutcome.Verified;

        switch (response.message) {
            case MfaMessage.ChallengeExpired:
                return MfaVerifyOutcome.Expired;
            case MfaMessage.TooManyAttempts:
                return MfaVerifyOutcome.TooManyAttempts;
            default:
                return MfaVerifyOutcome.InvalidCode;
        }
    }

    /** Setting 274's brand-wide MFA flag for Adora loyalty brands; it decides both the account-action gate and contact masking. */
    public isMfaEnabled(): boolean {
        return !!this._data.getBrandProperty("Settings")?.OOS?.showMfa && this._loyaltyProvider.getLoyaltyProvider() !== LoyaltyProvider.PUNCHH;
    }

    /** Contact EDIT, Change Password and Delete My Account need this tab verified once; the 30-day trust cookie never counts. */
    public readonly ensureAccountMfaAsync = async (): Promise<boolean> => {
        // A second click while the first is still starting a challenge would spend another from the per-IP limit.
        if (this._accountMfaInFlight)
            return false;

        this._accountMfaInFlight = true;
        try {
            return await this._ensureAccountMfaAsync();
        } finally {
            this._accountMfaInFlight = false;
        }
    }

    private async _ensureAccountMfaAsync(): Promise<boolean> {
        const profileId = this._user.getProperty("ProfileId") ?? 0;
        if (!this.isMfaEnabled() || TokenService.hasMfaSessionFor(profileId))
            return true;

        const openChallengeId = ProfileService._openAccountChallengeId(profileId);
        const start = await this._loyaltyProvider.startAccountMfa(openChallengeId).catch((): IAccountMfaStart => ({ challengeId: null }));
        if (start.message === MfaMessage.NotRequired)
            return true;

        if (!start.challengeId) {
            const outcome = start.message === MfaMessage.TooManyAttempts ? MfaVerifyOutcome.TooManyAttempts : MfaVerifyOutcome.Unavailable;
            this._mfaPrompt?.showEndingMessage(outcome);
            return false;
        }

        TokenService.setMfaAccountChallenge({ id: start.challengeId, profileId: profileId });
        return this._promptAccountMfaAsync(start.challengeId);
    }

    // The server renews this challenge's full lifetime instead of spending a new one from the per-IP limit.
    private static _openAccountChallengeId(profileId: number): string | null {
        const challenge = TokenService.getMfaAccountChallenge();
        return challenge?.profileId === profileId ? challenge.id : null;
    }

    private async _promptAccountMfaAsync(challengeId: string): Promise<boolean> {
        if (!this._mfaPrompt)
            return false;

        let sessionToken: string | undefined;
        const completed = await this._mfaPrompt.promptAsync({
            purpose: MfaPromptPurpose.AccountAction,
            sendCode: (contact: string) => this._loyaltyProvider.sendMfaCode(challengeId, contact),
            verifyCode: async (code: string) => {
                const verified = await this._loyaltyProvider.verifyAccountMfa(challengeId, code);
                sessionToken = verified.mfaSessionToken;
                const outcome = ProfileService._toMfaOutcome(verified);
                if (outcome !== MfaVerifyOutcome.InvalidCode)
                    TokenService.removeMfaAccountChallenge();
                return outcome;
            }
        });

        if (!completed || !sessionToken)
            return false;

        TokenService.setMfaSessionToken(sessionToken);
        return true;
    }

    // A refusal means the session token is missing or stale, so drop it and let the next gated action prompt again.
    private async _postWithMfaSessionAsync(url: string, payload: object): Promise<Response> {
        const response = await this.httpRequestAsync('POST', url, payload, { headers: TokenService.getMfaSessionHeaders() });
        if (response.status === HttpStatusCode.FORBIDDEN)
            TokenService.removeMfaSessionToken();
        return response;
    }

    /** Returns the response, not a boolean, so callers can tell a new profile from an existing one. */
    public readonly signInGoogle = async (idToken: string, rememberMe: boolean = true, nonce?: string | null): Promise<ISignInResponse> => {
        if (!this._loyaltyProvider.hasGoogleSignIn())
            return { success: false };

        const response = await this._loyaltyProvider.signInGoogle({
            store_key: this._data.getBrandProperty("StoreInfo").storeKey,
            location: "olo",
            id_token: idToken,
            remember_me: rememberMe,
            app: Util.isAppView(),
            nonce: nonce || undefined
        });

        await this._handleSignInResponseAsync(response);
        return response;
    }

    /** Nonce for the native Google Sign-In flow; null when the loyalty provider doesn't support it. */
    public readonly getGoogleSignInNonce = (): Promise<string | null> =>
        this._loyaltyProvider.getGoogleSignInNonce();

    public readonly signInSSO = async (token: string): Promise<boolean> => {
        if (this._loyaltyProvider.hasSingleSignOn()) {
            if (!token)
                return false;

            const response = await this._loyaltyProvider.signInSSO({ sso_token: token, location: "olo", store_key: this._data.getBrandProperty("StoreInfo").storeKey });
            return await this._handleSignInResponseAsync(response);
        }

        return false;
    }

    public readonly signInJWT = async (): Promise<boolean> => {
        const jwt = TokenService.getJWT();
        const refreshToken = TokenService.getRefreshToken();

        if (jwt && refreshToken) {
            const response = await this._loyaltyProvider.signInJWT();
            return await this._handleSignInResponseAsync(response);
        }

        return false;
    }

    public readonly signInPhone = async (data: ISignInPhone): Promise<boolean> => {
        const response = await this._loyaltyProvider.signInPhone(data);
        return await this._handleSignInResponseAsync(response);
    }

    /**
    * Signs the user out.
    * @async
    * @returns {Promise<boolean>} A promise that resolves with true if the sign out was successful, otherwise false.
    */
    public signOut = async (): Promise<boolean> => {
        if (TokenService.getJWT() !== "" && this._user.getProperty("ProfileId") != 0) {
            const response = await this.httpRequestAsync('POST', `${this._apiProfile}sign-out`);
            if (response.status == 200) {
                TokenService.removeJWT();
                TokenService.removeRefreshToken();
                TokenService.removeMfaSessionToken();
                TokenService.removeMfaAccountChallenge();

                GoogleSignInService.disableAutoSelect();

                if (Util.isAppView() && typeof deleteAppUser == "function")
                    deleteAppUser();

                await this._removeDiscountsAsync();
                this._user.user = ({} as IUser);
                this.user.setLoggedIn(false);

                this._eventBus.emit(EventTypes.CART_CHANGED);

                return true;
            }

            return false;
        }

        return false;
    }

    /**
     * Checks whether a user account already exists with the given email or phone number, for a logged-in user.
     * @async
     * @param email The email address to check for.
     * @param phone The phone number to check for.
     * @returns {Promise<number>} True if an account already exists with the given email or phone number, false otherwise. Null if the request failed.
     */
    public checkIfLoggedInExists = async (profileId: number, email: string, phone: string, countryId: number): Promise<number> => {
        const payload = await Util.encrypt(JSON.stringify({ storeKey: this._data.getBrandProperty("StoreInfo")?.storeKey, profileId: profileId, email: email, phone: phone, countryId: countryId }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}check`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? -10;
    }

    public getPhoneCode = async (phone: string, countryId: number): Promise<IGetPhoneCodeResponse> => {
        const payload = await Util.encrypt(JSON.stringify({ storeKey: this._data.getBrandProperty("StoreInfo")?.storeKey, phone: phone, countryId: countryId }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}get-phone-code`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, message: "" };
    }

    public updateUserDarkMode = (darkMode: boolean): void => {
        this._user.updateUser({ IsDarkMode: darkMode });
        this.httpRequestAsync('POST', `${this._apiProfile}update-user-dark-mode`, { darkMode: darkMode });
    }

    public updateUserLanguage = (language: string): void => {
        this._user.updateUser({ CultureCode: language });
        this.httpRequestAsync('POST', `${this._apiProfile}update-user-language`, { language: language });
    }

    /**************************
     ******** Password ********
     **************************/

    /**
     * Checks whether the user's current password matches the given password.
     * @async
     * @param oldPassword The user's current password.
     * @returns {Promise<boolean>} The response data from the server, or false if the request failed.
     */
    public checkPassword = async (oldPassword: string): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify({ oldPassword: oldPassword }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}check-password`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**
     * Changes the user's password.
     * @async
     * @param {string} oldPassword - The current password.
     * @param {string} newPassword - The new password.
     * @returns {Promise<boolean>} - A promise that resolves to `true` if the password was changed successfully, or `false` otherwise.
     */
    public changePassword = async (data: IChangePassword): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this._postWithMfaSessionAsync(`${this._apiProfile}change-password`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    };

    /**
    * Sends a reset password request to the server.
    * @async
    * @param {string} data - The data required for the reset password request.
    * @returns {Promise<boolean>} A promise that resolves with true if the reset password request was successful, otherwise false.
    */
    public resetPassword = async (data: IResetPassword): Promise<IResetPasswordResponse> => {
        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}reset-password`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? { success: false, isProfileComplete: false };
    }

    /**
     * Sends a password reset request for the specified user.
     * 
     * @param {ISendResetPassword} data - The user data for the password reset request.
     * @returns {Promise<boolean>} - True if the reset was successfully initiated, false otherwise.
     */
    public forgotPassword = async (email: string): Promise<boolean> => {
        const data: IForgotPassword = {
            store_key: this._data.getBrandProperty("StoreInfo")?.storeKey || "",
            email: email,
            is_olo3: true
        };

        const payload = await Util.encrypt(JSON.stringify(data), this._publicKey);
        const response = await this.httpRequestAsync("POST", `${this._apiLoyalty}olo/customer/forgot_password`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**
     * Saves a profile object to the API and invokes a callback function with a boolean indicating success or failure.
     * @async
     * @param {IaOLOUser} profile - The profile object to be saved.
     * @param {Function} callBack - Callback function to be invoked with a boolean indicating success or failure.
     * @returns {Promise<boolean>} - A promise that resolves with a boolean indicating success or failure.
     */
    public saveProfile = async (profile: IUser, callBack: Function, incompleteProfile: boolean = false): Promise<boolean> => {
        let saved = false;
        if (incompleteProfile)
            saved = await this._saveIncompleteProfile(profile);
        else
            saved = await this._setProfile(profile);

        if (callBack)
            callBack(saved);
        return saved;
    }

    private readonly _getProfile = async (): Promise<IGetProfileResponse | null> => {
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}profile`);
        return (response.status == 200) ? await this._http.getJson(response) : null;
    }

    /**
     * Sends a POST request to save a profile object to the API.
     * @async
     * @param {IaOLOUser} profile - The profile object to be saved.
     * @returns {Promise<boolean>} - A promise that resolves with the response data if the request is successful, or null if it fails.
     */
    private _setProfile = async (profile: IUser): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(this._mapOLOUser(profile)), this._publicKey);
        const response = await this._postWithMfaSessionAsync(`${this._apiProfile}save-profile`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**
     * Sends a POST request to delete a profile object to the API.
     * @async
     * @param {number} profileId - The profile object to be deleted.
     * @param {password} string - The profile object to be deleted.
     * @returns {Promise<boolean>} - A promise that resolves with the response data if the request is successful, or null if it fails.
     */
    public deleteProfile = async (profileId: number, password: string): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify({ "profileId": profileId, "password": password }), this._publicKey);
        const response = await this._postWithMfaSessionAsync(`${this._apiProfile}delete-profile`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }


    private _saveIncompleteProfile = async (profile: IUser): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify({ "storeKey": this._data.getProperty("StoreInfo")?.storeKey, "profile": profile }), this._publicKey);
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}save-incomplete-profile`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**************************
     ******* Locations ********
     **************************/

    /**
     * Saves a favorite store with the given storeKey and name.
     * @async
     * @param {string} storeKey - The key of the store to be saved.
     * @param {string} name - The name of the store to be saved.
     * @param {boolean} isDefaultStore - A boolean indicating whether the store is the user's default store.
     * @returns {Promise<boolean>} A Promise that resolves to a boolean indicating whether the store was saved successfully.
     */
    public saveFavoriteStore = async (storeKey: string): Promise<boolean> => {
        const loader = new LoaderSpinner();
        const response = await this.httpRequestAsync("POST", `${this._apiProfile}favorite-store`, { StoreKey: storeKey });
        loader?.destroy(1000);

        const json = response.status === 200 ? await this._http.getJson(response) : null;
        const saved = json ?? false;

        if (saved && this._user)
            this._user.getProperty("FavoriteStores")?.push(storeKey);

        return saved;
    }

    /**
     * Deletes a favorite store with the given storeKey.
     * @async
     * @param {string} storeKey - The key of the store to be deleted.
     * @returns {Promise<boolean>} A Promise that resolves to a boolean indicating whether the store was deleted successfully.
     */
    public deleteFavoriteStore = async (storeKey: string): Promise<boolean> => {
        const loader = new LoaderSpinner();
        const response = await this.httpRequestAsync("POST", `${this._apiProfile}favorite-store`, { StoreKey: storeKey });
        loader?.destroy(1000);

        if (response.status == 200) {
            const favorite = this._user.getProperty("FavoriteStores")?.filter(x => x !== storeKey) || [];
            this._user.setProperty("FavoriteStores", favorite);

            const json = response.status === 200 ? await this._http.getJson(response) : null;
            return json ?? false;
        }

        return false;
    }

    /**
     * Sends a request to the server to fetch the list of favorite stores.
     * @async
     * @returns {Promise<Array<IFetchFavoriteStoresResponse> | null>} A Promise that resolves to an array of objects, each containing the store key and name, or null if the request failed.
     */
    public fetchFavoriteStores = async (): Promise<IFetchFavoriteStoresResponse[]> => {
        const response = await this.httpRequestAsync("GET", `${this._apiProfile}favorite-store`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? [];
    }

    /**************************
     ******* My Orders ********
     **************************/

    /**
     * Asynchronously fetches orders from the API.
     * @async
     * @returns {Promise<IMyOrders[]>} - A Promise that resolves with an array of orders.
     */
    // public getOrders = async (): Promise<IMyOrders[]> => {
    //     const response = await this.httpRequestAsync("GET", `${this._apiProfile}profile-orders`);
    //     return (response.status == 200) ? await this._http.getJson(response) : [];
    // }

    /**
     * Fetches the details of the order with the given ID
     * @async
     * @param {number} orderId - The ID of the order to fetch details for
     * @return {Promise<IFetchOrderDetails | null>} A Promise that resolves to an object containing details of the order with the given ID, or null if the request failed or no order was found
     */
    public fetchOrderDetails = async (orderId: number): Promise<IFetchOrderDetails | null> => {
        const response = await this.httpRequestAsync("GET", `${this._apiProfile}order-detail/?orderId=${orderId}`);
        return (response.status == 200) ? await this._http.getJson(response) : null;
    }

    /**
     * Saves the given order as a favorite with the given name
     * @async
     * @param {number} orderID - The ID of the order to save as a favorite
     * @param {string} name - The name to save the favorite order under
     * @return {Promise<boolean>} A Promise that resolves to true if the operation succeeded, or false otherwise
     */
    public saveFavoriteOrder = async (orderID: number, name: string): Promise<boolean> => {
        const response = await this.httpRequestAsync("POST", `${this._apiProfile}toggle-favorite-order`, { OrderID: orderID, Name: name });
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**************************
     ******* Favorite items *******
     **************************/

    public getFavoriteItems = async (): Promise<IUserFavoriteItem[]> => {
        const response = await this.httpRequestAsync("GET", `${this._apiProfile}favorite-items`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? [];
    }

    public saveFavoriteItem = async (favItem: IUserFavoriteItem): Promise<number> => {
        const response = await this.httpRequestAsync("POST", `${this._apiProfile}favorite-item`, favItem);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? 0;
    }

    public deleteFavoriteItem = async (favitemId: number): Promise<boolean> => {
        const response = await this.httpRequestAsync("DELETE", `${this._apiProfile}favorite-item?favItemId=${favitemId}`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**************************
     ******* My History *******
     **************************/

    /**
     * Fetches loyalty history data for the specified date.
     * @async
     * @param {string} historyDate - The date for which to fetch the loyalty history, in the format "YYYY-MM-DD".
     * @returns {Promise<IMyHistory|null>} - A Promise that resolves with the loyalty history data, or null if the request fails.
     */
    public fetchLoyaltyHistory = async (historyDate: string): Promise<IMyHistory[] | null> => {
        const response = await this.httpRequestAsync("GET", `${this._apiProfile}history/?date=${historyDate}`);
        return (response.status == 200) ? await this._http.getJson(response) : null;
    }

    /**************************
     ******* My Loyalty *******
     **************************/
    public getNextRewardsInfo = async (): Promise<{ points: number, reward: IDataReward | null }> => {
        return this._loyaltyProvider.getPointsToNextReward();
    }

    /**************************
     ****** Unsubscribe *******
     **************************/
    public unsubscribe = async (params: IUnsubscribeDto): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify(params), this._publicKey);
        const spinner = new LoaderSpinner();
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}unsubscribe`, payload);
        spinner.destroy();
        return (response.status == 200);
    }

    public resubscribe = async (storeKey: string, profileId: number): Promise<boolean> => {
        const payload = await Util.encrypt(JSON.stringify({ storeKey: storeKey, profileId: profileId }), this._publicKey);
        const spinner = new LoaderSpinner();
        const response = await this.httpRequestAsync('POST', `${this._apiProfile}resubscribe`, payload);
        spinner.destroy();
        return (response.status == 200);
    }

    /**************************
     ******** Wallet **********
     **************************/
    public async getUserWalletAsync(): Promise<void> {
        const wallet = await this._getWalletAsync();
        this._user.setProperty("Wallet", wallet);
        this._eventBus.emit(EventTypes.USER_CHANGED);
    }

    private async _getWalletAsync(): Promise<IUserCard[]> {
        const response = await this.httpRequestAsync("GET", `${this._apiProfile}wallet`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? [];
    }

    /**************************************
     ******** Single-use coupons *********
     **************************************/

    /**
     * Fetches the customer's Customer.Api single-use coupon wallet and stores it on the user.
     */
    public async getSingleUseCouponWalletAsync(): Promise<void> {
        const coupons = await this._fetchSingleUseCouponWalletAsync();
        this._user.setProperty("SingleUseCoupons", coupons);
        this._eventBus.emit(EventTypes.USER_CHANGED);
    }

    private async _fetchSingleUseCouponWalletAsync(): Promise<ISingleUseCouponWalletEntry[]> {
        try {
            const response = await this.httpRequestAsync("POST", `${this._apiProfile}single-use-coupons`);
            const json = response.status === 200 ? await this._http.getJson(response) : null;
            return json?.coupons ?? [];
        } catch {
            return [];
        }
    }


    public deleteWallet = async (last4: string): Promise<boolean> => {
        const response = await this.httpRequestAsync("DELETE", `${this._apiProfile}wallet?last4=${last4}`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    /**************************
     **** Landing Banner ******
     **************************/

    /**
     * Loads the banner data associated with the given store key
     * @async
     * @param {string} storeKey 
     * @return {Promise<string>} 
     */
    public getLandingBanner = async (storeKey: string | null | undefined): Promise<string> => {
        if (!storeKey)
            return '{}';

        const response = await this.httpRequestAsync("GET", `${this._apiProfile}landing-banner/?storeKey=${storeKey}`);
        if (response.status == 200) {
            const json = await this._http.getJson(response);
            if (json?.BAN && json.BAN != "")
                return json.BAN;
        }
        return '{}';
    }

    /****************************
     **** Delivery Address ******
     ****************************/

    public addDeliveryAddress = async (tempAddress: ITempAddress): Promise<boolean> => {
        const mapAddress = (temp: ITempAddress): InOrderAddress => {
            const address: InOrderAddress = { ...temp } as InOrderAddress;
            address.Address1 = address.StreetName;
            address.CustomerID = this.user.getProperty("CustomerId") || 0;

            return address;
        }

        const payload = await Util.encrypt(JSON.stringify(mapAddress(tempAddress)), this._publicKey);

        const response = await this.httpRequestAsync("POST", `${this._apiProfile}delivery-address`, payload);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

    public deleteDeliveryAddress = async (addressId: number): Promise<boolean> => {
        const response = await this.httpRequestAsync("DELETE", `${this._apiProfile}delivery-address?addressId=${addressId}`);
        const json = response.status === 200 ? await this._http.getJson(response) : null;
        return json ?? false;
    }

  /******************
  **** Credits ******
  *******************/

    public updateProfileCredits = async (): Promise<boolean> => {
        const response = await this._getProfile();
        if (response?.success) {
            const data = JSON.parse(response.profile);
            const updatedNextOrderCredits = data.CCRDs?.map((cred: IaOLOUserNextOrderCredit) => ({
                CustomerCreditID: cred.CustomerCreditID,
                CouponID: cred.CouponID,
                AddedDate: cred.AddedDate,
                ExpirationDate: cred.ExpirationDate,
                UsedDate: cred.UsedDate,
                StoreID: cred.StoreID,
                Used: cred.IsUsed
            })) ?? null;
            
            this._user.updateUser({ NextOrderCredits: updatedNextOrderCredits });
            return true;
        }

        return false;
    }


    // #84813 -- resolves the E.164 full phone here so Identity's shadow-sync doesn't need its own
    // calling-code lookup; CountryCode in the Countries list already carries the "+" prefix.
    private _resolveFullPhone(phone?: string, countryCodeId?: number): string | null {
        if (!phone) return null;
        const countries = this._data.getBrandProperty("Countries");
        const country = Array.isArray(countries) ? countries.find(c => c.CountryID === Number(countryCodeId)) : null;
        return country ? `${country.CountryCode}${phone}` : null;
    }

    private _mapProfile = (profile: IProfile): IaOLOUser => {
        const user = {
            CID: profile?.CustomerId,
            PID: profile?.ProfileId,
            LRID: profile?.LoyaltyReferenceID,
            SID: profile?.StoreId,
            NAM: profile?.Name,
            LNAM: profile?.LastName,
            PHN: profile?.Phone,
            CCDID: Number(profile?.CountryCodeId),
            FPHN: this._resolveFullPhone(profile?.Phone, profile?.CountryCodeId),
            EML: profile?.Email,
            ISV: profile?.IsMarketingSurvey,
            IEML: profile?.IsMarketingEmail,
            ITXT: profile?.IsMarketingText,
            MKPHN: profile?.MarketingPhone,
            MKCCDID: Number(profile?.MarketingCountryCodeId),
            BD: profile?.BirthDate,
            ILY: profile?.IsLoyalty,
            IBLKD: profile?.IsBlocked,
            CLT: profile?.CultureCode,
            IDK: this._context.getProperty("darkMode"),
            ALGS: profile?.Allergies,
            DITS: profile?.Dietary,
            pwd: profile?.pwd,
            TPP: profile?.TermsPrivacyPolicy,
            A13: profile?.IsAge13,
            FAVST: false,
            FAVSTID: profile?.DefaultStoreId,
            FAVSTRs: [],
            DFSTKEY: profile?.DefaultStoreKey,
            WLTS: [],
            ADRS: profile?.Addresses?.map(x => {
                return {
                    AID: x.AID,
                    STRNO: x.STRNO,
                    ADDR1: x.ADDR1,
                    ADDR2: x.ADDR2,
                    ADDR3: x.ADDR3,
                    ADDR4: x.ADDR4,
                    ADDR5: x.ADDR5,
                    XSTR: x.XSTR,
                    CITY: x.CITY,
                    STA: x.STA,
                    ZIP: x.ZIP,
                    CID: x.CID,
                    LAT: x.LAT,
                    LON: x.LON,
                    IPRM: x.IPRM,
                    ATID: 0,
                    ANAM: "",
                };
            }) || []
        };

        return user;
    }

    public get profile(): IProfile {
        return {
            CustomerId: this._user.getProperty("CustomerId") || 0,
            LoyaltyReferenceID: this._user.getProperty("LoyaltyReferenceID") || null,
            Name: this._user.getProperty("Name") || "",
            LastName: this._user.getProperty("LastName") || "",
            Phone: this._user.getProperty("Phone") || "",
            CountryCodeId: this._user.getProperty("CountryCodeId") || 1,
            Email: this._user.getProperty("Email") || "",
            pwd: "",
            repeatPwd: "",
            BirthDate: this._user.getProperty("BirthDate") || null,
            BirthMonth: 1,
            BirthDay: 1,
            IsMarketingEmail: this._user.getProperty("IsMarketingEmail") || false,
            IsMarketingText: this._user.getProperty("IsMarketingText") || false,
            MarketingPhone: this._user.getProperty("MarketingPhone") || "",
            MarketingCountryCodeId: this._user.getProperty("MarketingCountryId") || 1,
            IsMarketingSurvey: this._user.getProperty("IsMarketingSurvey") || false,
            IsLoyalty: this._data.getLoyaltyInfo().providerId != LoyaltyProvider.NONE,
            IsBlocked: false,
            StoreId: this._user.getProperty("StoreId") || 0,
            ProfileId: this._user.getProperty("ProfileId") || 0,
            DefaultStoreId: 0,
            DefaultStoreKey: "",
            CultureCode: this._context.getProperty("languageCode"),
            IsAge13: this._user.getProperty("IsAge13") || false,
            TermsPrivacyPolicy: this._user.getProperty("TermsPrivacyPolicy") || false,
            Addresses: this._user.getProperty("Addresses")?.map(a => a as IProfileAddress) || [],
            Dietary: this._user.getProperty("Dietary")?.map(d => d as IProfileDietary) || [],
            Allergies: this._user.getProperty("Allergies")?.map(a => a as IProfileAllergy) || [],
        }
    }

    private readonly _mapOLOUser = (data: IUser): IaOLOUser => {
        const user: IaOLOUser = {
            CID: data.CustomerId || 0,
            PID: data.ProfileId || 0,
            SID: data.StoreId || 0,
            NAM: data.Name || "",
            LNAM: data.LastName || "",
            PHN: data.Phone || "",
            CCDID: data.CountryCodeId || 1,
            EML: data.Email || "",
            ISV: !!data.IsMarketingSurvey,
            IEML: !!data.IsMarketingEmail,
            ITXT: !!data.IsMarketingText,
            MKPHN: data.MarketingPhone || "",
            MKCCDID: data.MarketingCountryId || 1,
            BD: data.BirthDate || null,
            FAVST: !!data.IsFavoriteStore,
            FAVSTID: data.FavoriteStoreId || 0,
            FAVSTRs: data.FavoriteStores?.map((x: string) => ({ SKEY: x } as IaOLOUserFavoriteStore)) || [],
            CLT: data.CultureCode || "",
            IDK: !!data.IsDarkMode,
            ILY: !!data.IsLoyalty,
            IBLKD: !!data.IsBlocked,
            A13: data.IsAge13,
            TPP: data.TermsPrivacyPolicy,
            ADRS: data.Addresses || [],
            ALGS: data.Allergies || [],
            DITS: data.Dietary || [],
            WLTS: data.Wallet.map(x => ({
                ADMID: x.adoraMerchandId,
                BADR: x.billingAddress,
                BZIP: x.billingZip,
                CCL4: x.last4,
                CCTID: x.cardTypeId,
                CTDAT: x.tokenDate,
                CTKN: x.token,
                EXPMNT: x.expirationMonth,
                EXPYR: x.expirationYear,
                EXPMNTNUM: x.expirationMonthNumber,
                EXPYRNUM: x.expirationYearNumber,
                MCHSD: x.merchantSpecData,
                SID: x.storeId
            })),
            IPC: data.IsProfileComplete,
            LOYDT:
                //data.LoyaltyData ? {
                //    rewards: data.LoyaltyData.Rewards?.map((reward: IUserLoyaltyReward) => ({
                //        RID: reward.RewardId,
                //        CPID: reward.CouponId,
                //        EXDT: reward.ExpiryDate,
                //        EXP: reward.Expired,
                //        NAMs: reward.Names,
                //        EDT: "",
                //        RD: false,
                //        DSCRs: [],
                //        discount_amount: null
                //    })) ||
                //        [],
                //    current_points: data.LoyaltyData.CurrentPoints,
                //    earned_points: data.LoyaltyData.EarnedPoints,
                //    points_last_updated_date: data.LoyaltyData.PointsLastUpdatedDate,
                //    loyalty_joined_date: data.LoyaltyData.LoyaltyJoinedDate,
                //    tier: data.LoyaltyData.TierId,
                //    tier_name: data.LoyaltyData.TierName,
                //    banked_currency: data.LoyaltyData.BankedCurrency,
                //    unbanked_points: data.LoyaltyData.UnbankedPoints,
                //    TNAMs: [],
                //    NTNAMs: [],
                //    SRWs: [],
                //    redeemed_points: 0,
                //    expired_points: 0,
                //    point_rate: 0,
                //    NMT: 0,
                //    } :
                undefined,
            Offers: undefined,
            DEFST: data.DefaultStore ? {
                SKEY: data.DefaultStore.StoreKey,
                STRNO: data.DefaultStore.StreetNumber,
                ADDR1: data.DefaultStore.Address1,
                ADDR2: data.DefaultStore.Address2,
                CID: data.DefaultStore.CountryId
            } : undefined,
            DFSTKEY: data.DefaultStore?.StoreKey || null
        };

        return user;
    }

    private readonly _mapUser = (data: IaOLOUser): IUser => {
        const user: IUser = {
            CustomerId: data.CID,
            ProfileId: data.PID,
            StoreId: data.SID,
            Name: data.NAM,
            LastName: data.LNAM,
            Phone: data.PHN,
            CountryCodeId: data.CCDID,
            Email: data.EML,
            IsMarketingSurvey: data.ISV,
            IsMarketingEmail: data.IEML,
            IsMarketingText: data.ITXT,
            MarketingPhone: data.MKPHN,
            MarketingCountryId: data.MKCCDID,
            BirthDate: data.BD ? new Date(data.BD) : null,
            IsFavoriteStore: data.FAVST,
            FavoriteStoreId: data.FAVSTID,
            FavoriteStores: data.FAVSTRs?.map((x: IaOLOUserFavoriteStore) => x.SKEY) || [],
            CultureCode: data.CLT,
            IsDarkMode: data.IDK,
            IsLoyalty: data.ILY,
            IsBlocked: data.IBLKD,
            IsGoogleAccount: data.IGOOG,
            HasPassword: data.HASPWD,
            IsAge13: data.A13,
            TermsPrivacyPolicy: data.TPP,
            Addresses: data.ADRS,
            Allergies: data.ALGS,
            Dietary: data.DITS,
            Wallet: [],
            IsProfileComplete: data.IPC,
            LoyaltyData: data.LOYDT ? {
                Rewards: data.LOYDT.rewards?.map((reward: IaOLOUserLoyaltyReward) => ({
                    RewardId: reward.RID,
                    CouponId: reward.CPID,
                    ExpiryDate: reward.EXDT,
                    Expired: reward.EXP,
                    Names: reward.NAMs
                })) || [],
                CurrentPoints: data.LOYDT.current_points,
                EarnedPoints: data.LOYDT.earned_points,
                PointsLastUpdatedDate: data.LOYDT.points_last_updated_date,
                LoyaltyJoinedDate: data.LOYDT.loyalty_joined_date,
                TierId: data.LOYDT?.tier,
                TierName: data.LOYDT?.tier_name,
                BankedCurrency: data.LOYDT?.banked_currency,
                UnbankedPoints: data.LOYDT?.unbanked_points,
            } : null,
            Offers: data.Offers ? {
                Codes: data.Offers.Codes ?? [],
                Coupons: data.Offers.Coupons?.map((c: IaOLOUserCoupon) => ({
                    CouponId: c.id,
                    Names: c.Names,
                    Descriptions: c.descr,
                    Discount: c.Discount,
                    CouponAvailabilities: c.avail?.map((a: IaOLOUserCouponAvailability) => ({
                        StoreKey: a.key,
                        CouponId: a.couponId,
                        IsHidden: a.hidden,
                        IsOfferedOnLine: a.offline,
                        IsHiddenOnLine: a.hiddOnline,
                        IsActive: a.active,
                        IsBoxTopper: a.boxTopp,
                        BoxTopperValidDays: a.boxToppValid,
                        IsAutoApply: a.auto,
                        IsScheduled: a.sched,
                        IsRequired: a.required,
                        StartDate: a.start,
                        EndDate: a.end,
                        IsVisibleInCouponList: a.visible,
                        CouponName: a.cName,
                    })) || [],
                    NoOfActiveStores: c.NoOfActiveStores,
                    StartDate: c.start,
                    EndDate: c.end,
                    IsOfferedOnline: c.offline,
                    IsOfferedInStore: c.inStore,
                    IsHidden: c.hidder,
                    IsOnlyAvailableApp: c.app,
                    IsProfileRequired: c.profileReq,
                    IsAutoApply: c.auto,
                })) || []
            } : null,
            NextOrderCredits: data.CCRDs?.map((cred: IaOLOUserNextOrderCredit) => ({
                CustomerCreditID: cred.CustomerCreditID,
                CouponID: cred.CouponID,
                AddedDate: cred.AddedDate,
                ExpirationDate: cred.ExpirationDate,
                UsedDate: cred.UsedDate,
                StoreID: cred.StoreID,
                Used: cred.IsUsed
            })) || null,
            DefaultStore: data.DEFST ? {
                StoreKey: data.DEFST.SKEY,
                StreetNumber: data.DEFST.STRNO,
                Address1: data.DEFST.ADDR1,
                Address2: data.DEFST.ADDR2,
                CountryId: data.DEFST.CID
            } : null
        };

        return user;
    }

    /**
     * Combines user offers, rewards, and single-use wallet coupons into a unified list.
     * @param {IUserOffers | null} offers - The user's available offers.
     * @param {IUserLoyaltyReward[]} rewards - The user's loyalty rewards.
     * @param {boolean} enforceTimeSensitive - Whether to enforce time-sensitive checks on offers.
     * @param {ISingleUseCouponWalletEntry[]} walletCoupons - Coupons already in the user's Customer.Api wallet.
     * @returns {IUserRewardOffersDTO[]} - A sorted list of combined offers, rewards, and wallet coupons.
     */
    public async combineUserOffers(
        offers: IUserOffers | null,
        rewards: IUserLoyaltyReward[],
        enforceTimeSensitive: boolean = false,
        walletCoupons: ISingleUseCouponWalletEntry[] = []
    ): Promise<IUserRewardOffersDTO[]> {
        const combinedOffers: IUserRewardOffersDTO[] = [
            ...this._createCombinedFromRewards(rewards),
            ...await this._createCombinedFromOffers(offers, enforceTimeSensitive),
            ...this._createCombinedFromWallet(walletCoupons)
        ];

        //sort by expiration date, then by offerId if expiration date is the same
        combinedOffers.sort((a, b) => {
            if (a.ExpiryDate === null && b.ExpiryDate !== null) {
                return 1;
            } else if (a.ExpiryDate !== null && b.ExpiryDate === null) {
                return -1;
            } else if (a.ExpiryDate !== null && b.ExpiryDate !== null && a.ExpiryDate !== b.ExpiryDate) {
                return new Date(a.ExpiryDate).getTime() - new Date(b.ExpiryDate).getTime();
            } else {
                return a.OfferId > b.OfferId ? -1 : 1;
            }
        });

        return combinedOffers;
    }

    /**
     * Creates a list of combined reward offers.
     * @param {IUserLoyaltyReward[]} rewards - The user's loyalty rewards.
     * @returns {IUserRewardOffersDTO[]} - A list of formatted reward offers.
     */
    private _createCombinedFromRewards(rewards: IUserLoyaltyReward[]): IUserRewardOffersDTO[] {
        return rewards
            .filter(x => !x.Expired)
            .map(x => ({
                OfferId: x.RewardId.toString(),
                IsReward: true,
                IsCredit: false,
                ExpiryDate: x.ExpiryDate ? new Date(x.ExpiryDate) : null,
                Used: x.Used || false,
                Names: x.Names
            }));
    }

    /**
     * Creates combined offers from the user's single-use coupon wallet -- already-owned, so no
     * schedule check applies; falls back to the coupon code if POS has no name for it.
     * @param {ISingleUseCouponWalletEntry[]} walletCoupons - Coupons in the user's Customer.Api wallet.
     * @returns {IUserRewardOffersDTO[]} - A list of formatted wallet coupon offers.
     */
    private _createCombinedFromWallet(walletCoupons: ISingleUseCouponWalletEntry[]): IUserRewardOffersDTO[] {
        const incompleteDiscounts = this._order.getIncompleteDiscounts();
        return walletCoupons.map(entry => {
            const code = entry.couponCode.toLowerCase();
            const isApplied = this._order.getProperty("Coupons")?.some(x => x.CouponCode.toLowerCase() === code) ?? false;
            const isIncomplete = incompleteDiscounts.some(x => (x.couponCode ?? '').toLowerCase() === code);

            return {
                OfferId: entry.couponCode,
                IsReward: false,
                IsCredit: false,
                ExpiryDate: new Date(entry.expirationDate),
                // Reflects the order's current state, not just "assigned" -- otherwise navigating away
                // and back to Rewards always shows Redeem, even for a still-applied/pending coupon.
                Used: isApplied || isIncomplete,
                Names: entry.names.length > 0
                    ? entry.names.map(x => ({ CULT: x.culture, NAM: x.name }))
                    : [{ CULT: "en-US", NAM: entry.couponCode }],
                SingleUseCouponCode: entry.couponCode
            };
        });
    }

    /**
     * Creates a list of combined user offers.
     * @param {IUserOffers | null} offers - The user's available offers.
     * @param {boolean} enforceTimeSensitive - Whether to enforce time-sensitive checks on offers.
     * @returns {IUserRewardOffersDTO[]} - A list of formatted user offers.
     */
    private async _createCombinedFromOffers(
        offers: IUserOffers | null,
        enforceTimeSensitive: boolean
    ): Promise<IUserRewardOffersDTO[]> {
        if (!offers) return [];

        const now = Util.NowStore(this._data.getProperty("TimeOffset"));

        const couponById = new Map<number, typeof offers.Coupons[number]>();
        for (const c of offers.Coupons) couponById.set(c.CouponId, c);

        const scheduleCache = new Map<number, boolean>();
        const out: IUserRewardOffersDTO[] = [];

        for (const code of offers.Codes) {
            if (this._isOfferExpired(code.Expires, now)) continue;

            const offerCoupon = couponById.get(code.CouponId);
            if (!offerCoupon) continue;

            if (!(await this._isOfferScheduleOk(offerCoupon.CouponId, enforceTimeSensitive, scheduleCache))) continue;

            out.push({
                OfferId: code.CouponCode,
                IsReward: false,
                IsCredit: false,
                ExpiryDate: code.Expires ? code.Expires : null,
                Used: code.Used,
                Names: offerCoupon.Names.map(x => ({ CULT: x.lc, NAM: x.text }))
            });
        }

        return out;
    }

    /**
     * Determines if an offer has expired based on its expiration date and the current date.
     * @param expires
     * @param now
     * @returns
     */
    private _isOfferExpired(expires: Date | null | undefined, now: Date): boolean {
        return !!expires && expires < now;
    }

    /**
     * Determines if an offer has a time sensitive schedule and if it is valid.
     * @param couponId
     * @param enforce
     * @param cache
     * @returns
     */
    private async _isOfferScheduleOk(
        couponId: number,
        enforce: boolean,
        cache: Map<number, boolean>
    ): Promise<boolean> {
        if (!enforce) return true;

        const cached = cache.get(couponId);
        if (cached !== undefined) return cached;

        const ok = await this._order.checkCouponIdScheduleForOffer(couponId);
        cache.set(couponId, ok);
        return ok;
    }

    /**
     * Handles the redeem button click event for an offer or reward.
     * @param {string} [rewardId] - The ID of the reward to redeem, if applicable.
     * @param {string} [couponCode] - The coupon code of the offer to redeem, if applicable.
     * @returns {Promise<{ offerId: string | undefined, isUsed: boolean }>} - The result of the redemption attempt.
     */
    public async redeemOfferAsync(rewardId?: string, couponCode?: string, creditId?: string): Promise<{ offerId: string | undefined, isUsed: boolean }> {
        if (!this._data.getProperty("StoreInfo").storeKey) {
            this._eventBus.emit(EventTypes.LOCATION_CHANGE, Pages.LOCATIONS, { rewardToRedeem: { rewardId, couponCode } });
            return { offerId: undefined, isUsed: false };
        }

        let isUsed = false;
        let offerId: string | undefined = undefined;

        if (rewardId) {
            const reward = this.user.getRewardInfo(Number(rewardId));
            if (reward) {
                offerId = rewardId;
                isUsed = await this._order.processRewardAsync(reward, this.loyaltyProvider);
            }
        }

        if (couponCode) {
            const offer = this.user.getOfferInfo(couponCode);
            if (offer) {
                offerId = couponCode;
                isUsed = await this._order.processOfferAsync(offer, this.loyaltyProvider);
            } else if (this.user.getSingleUseCouponInfo(couponCode)) {
                offerId = couponCode;
                isUsed = await this._order.applyCouponByCodeAsync(this.loyaltyProvider, couponCode);
            }
        }

        if (creditId) {
            const credit = this.user.getCreditInfo(creditId);
            if (credit) {
                offerId = credit.CustomerCreditID.toString();
                isUsed = await this._order.processCreditAsync(this._loyaltyProvider, credit);
            }
        }

        return { offerId, isUsed };
    }

    /**
     * Removes all applied discounts and coupons from the order.
     * Ensures that reward-based and banked currency coupons are also removed.
     * @returns {Promise<void>}
     */
    private async _removeDiscountsAsync(): Promise<void> {
        const orderCoupons = this._order.getProperty("Coupons");
        const offers = this._user.getProperty("Offers");

        for (let i = orderCoupons.length - 1; i >= 0; i--) {
            const coupon = orderCoupons[i];
            let offer: IUserCampaignCodesData | null = null;

            if (offers && offers.Codes.length > 0)
                offer = offers.Codes.find(x => x.CouponCode.toLowerCase() == coupon.CouponCode) || null;

            if (coupon.RewardId || coupon.IsBankedCurrency || offer || coupon.CouponId === CouponIdList.LOYALTY_REDEEM_ITEM)
                await this._order.removeCouponByKeyAsync(this._loyaltyProvider, coupon.CouponKey, false, false);
        }
    }
}